import { z } from 'zod';
import { apiGet } from '@/lib/api';

export const LOG_LEVELS = ['DEBUG', 'INFO', 'WARN', 'ERROR'] as const;
export type LogLevel = (typeof LOG_LEVELS)[number];

const DiagnosticRowSchema = z.object({
  seq: z.string(),
  at: z.string(),
  level: z.enum(LOG_LEVELS),
  source: z.string(),
  groupId: z.string().nullable(),
  message: z.string(),
  context: z.unknown(),
});

export type DiagnosticRow = z.infer<typeof DiagnosticRowSchema>;

const LimitsSchema = z.object({ maxDays: z.number(), maxRows: z.number() });

const DiagnosticListSchema = z.object({
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  limits: LimitsSchema,
  rows: z.array(DiagnosticRowSchema),
});

const AuditRowSchema = z.object({
  seq: z.string(),
  at: z.string(),
  actorId: z.string().nullable(),
  /** ODPIS tożsamości z chwili zdarzenia — to jest autorytatywne „kto", nie `actorId` */
  actorEmail: z.string(),
  actorName: z.string().nullable(),
  action: z.string(),
  subjectType: z.string(),
  subjectId: z.string().nullable(),
  groupId: z.string().nullable(),
  outcome: z.enum(['OK', 'DENIED']),
  detail: z.unknown(),
});

export type AuditRow = z.infer<typeof AuditRowSchema>;

const AuditListSchema = z.object({
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  retention: z.string(),
  rows: z.array(AuditRowSchema),
});

const StatsSchema = z.object({
  diagnostic: z.object({
    total: z.number(),
    oldestAt: z.string().nullable(),
    limits: LimitsSchema,
    usedPct: z.number().nullable(),
    byLevel: z.record(z.string(), z.number()),
  }),
  audit: z.object({ total: z.number(), oldestAt: z.string().nullable(), rotated: z.boolean() }),
});

export type LogStats = z.infer<typeof StatsSchema>;

export type DiagnosticQuery = {
  groupId?: string;
  minLevel?: LogLevel;
  source?: string;
  search?: string;
  page: number;
  pageSize: number;
};

export type AuditQuery = {
  groupId?: string;
  action?: string;
  outcome?: 'OK' | 'DENIED';
  actor?: string;
  page: number;
  pageSize: number;
};

/**
 * Puste filtry NIE lecą do API. Schemat po stronie serwera jest `.strict()`, a `?source=`
 * przeszłoby jako „źródło o pustej nazwie" i wycięłoby wszystko.
 */
function qs(q: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  return p.toString();
}

export const fetchLogStats = () => apiGet('/logs/stats', StatsSchema);

export const fetchDiagnostic = (q: DiagnosticQuery) =>
  apiGet(`/logs/diagnostic?${qs({ ...q })}`, DiagnosticListSchema);

export const fetchAudit = (q: AuditQuery) => apiGet(`/logs/audit?${qs({ ...q })}`, AuditListSchema);
